import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, IndianRupee, Download } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { LineChartCard, BarChartCard, PieChartCard, AreaChartCard } from '../components/common/Charts';
import api from '../services/api';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
}

export function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const analytics = await api.getAnalyticsOverview();
        setData(analytics);
      } catch (e) {
        console.error('Failed to load analytics:', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  if (isLoading) {
    return (
      <div data-testid="analytics-page" className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="analytics-page" className="space-y-8 animate-fade-in">
        <PageHeader title="Analytics" description="Insights and reports on attendance, fees, and enrollment." />
        <DataCard className="text-center py-16">
          <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Unable to load analytics data</p>
        </DataCard>
      </div>
    );
  }

  const hasAttendanceData = data.monthlyAttendance?.some(m => m.rate > 0);
  const hasFeeData = data.feeStats?.totalInvoices > 0;

  return (
    <div data-testid="analytics-page" className="space-y-8 animate-fade-in">
      <PageHeader
        title="Analytics"
        description="Insights and reports on attendance, fees, and enrollment."
      />

      {/* Top Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Students"
          value={data.totalStudents || 0}
          icon={Users}
          delay={0}
        />
        <StatsCard
          title="Attendance Rate"
          value={`${data.attendanceRate || 0}%`}
          description="last 30 days"
          icon={BarChart3}
          delay={0.05}
        />
        <StatsCard
          title="Fee Collection"
          value={`${data.collectionRate || 0}%`}
          description={`${formatCurrency(data.feeStats?.collected)} of ${formatCurrency(data.feeStats?.totalFees)}`}
          icon={IndianRupee}
          delay={0.1}
        />
        <StatsCard
          title="Teachers"
          value={data.teacherCount || 0}
          icon={TrendingUp}
          delay={0.15}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DataCard title="Monthly Attendance Trend" subtitle="Attendance rate over time" delay={0.2}>
          {hasAttendanceData ? (
            <LineChartCard data={data.monthlyAttendance} xKey="month" yKey="rate" />
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
              No attendance data available yet
            </div>
          )}
        </DataCard>
        <DataCard title="Attendance by Class" subtitle="30-day attendance rate per class" delay={0.25}>
          {data.attendanceByClass?.length > 0 ? (
            <BarChartCard data={data.attendanceByClass} xKey="subject" yKey="score" />
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
              No class-wise data available
            </div>
          )}
        </DataCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DataCard title="Student Performance Heat Map" subtitle="Topic-wise average scores" delay={0.3}>
          {(() => {
            const heatmapData = [
              { topic: 'Algebra', avgScore: 78, status: 'Strong' },
              { topic: 'Geometry', avgScore: 52, status: 'Moderate' },
              { topic: 'Organic Chemistry', avgScore: 35, status: 'Weak' },
              { topic: 'Thermodynamics', avgScore: 61, status: 'Moderate' },
              { topic: 'Grammar', avgScore: 85, status: 'Strong' },
              { topic: 'Trigonometry', avgScore: 42, status: 'Weak' },
              { topic: 'Modern History', avgScore: 71, status: 'Strong' },
              { topic: 'Cell Biology', avgScore: 55, status: 'Moderate' },
            ];
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left p-2 text-xs font-medium text-muted-foreground">Topic</th>
                      <th className="text-center p-2 text-xs font-medium text-muted-foreground">Avg Score</th>
                      <th className="text-center p-2 text-xs font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData.map((row, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-800" data-testid={`heatmap-row-${i}`}>
                        <td className="p-2 text-sm">{row.topic}</td>
                        <td className="p-2 text-center font-medium">{row.avgScore}%</td>
                        <td className="p-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            row.avgScore >= 70 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                            row.avgScore >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>{row.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </DataCard>
        <DataCard title="Weekly Attendance" subtitle="Last 4 weeks" delay={0.35}>
          {data.weeklyAttendance?.some(w => w.rate > 0) ? (
            <AreaChartCard data={data.weeklyAttendance} xKey="week" yKey="rate" />
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
              No weekly data available
            </div>
          )}
        </DataCard>
      </div>

      {/* Fee Insights */}
      {hasFeeData && (
        <DataCard title="Fee Collection Insights" delay={0.4}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-xl bg-emerald-500/10">
              <IndianRupee className="w-8 h-8 text-emerald-500 mb-3" />
              <h4 className="font-semibold text-foreground">Collected</h4>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{formatCurrency(data.feeStats.collected)}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {data.feeStats.paidInvoices} of {data.feeStats.totalInvoices} invoices paid
              </p>
            </div>
            <div className="p-4 rounded-xl bg-red-500/10">
              <IndianRupee className="w-8 h-8 text-red-500 mb-3" />
              <h4 className="font-semibold text-foreground">Pending</h4>
              <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(data.feeStats.pending)}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {data.feeStats.totalInvoices - data.feeStats.paidInvoices} unpaid invoices
              </p>
            </div>
            <div className="p-4 rounded-xl bg-brand-light-blue/10">
              <TrendingUp className="w-8 h-8 text-brand-light-blue mb-3" />
              <h4 className="font-semibold text-foreground">Collection Rate</h4>
              <p className="text-2xl font-bold text-brand-light-blue mt-1">{data.collectionRate}%</p>
              <p className="text-sm text-muted-foreground mt-1">
                Total invoiced: {formatCurrency(data.feeStats.totalFees)}
              </p>
            </div>
          </div>
        </DataCard>
      )}
    </div>
  );
}
